// DataGrid — virtualized, sortable, multi-select table.
// Row height inherits from CSS var `--row-height` controlled by <html data-density>.

import { useRef, type CSSProperties, type ReactNode } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { cn } from "@/lib/cn";

export type SortDir = "asc" | "desc";
export interface SortState { key: string; dir: SortDir }

export interface DataGridColumn<T> {
  key: string;
  label: string;
  width?: number | string;
  align?: "left" | "right" | "center";
  sortable?: boolean;
  render?: (row: T) => ReactNode;
}

export interface DataGridProps<T> {
  rows: T[];
  columns: DataGridColumn<T>[];
  rowKey: (row: T) => string;
  sort?: SortState | null;
  onSort?: (sort: SortState) => void;
  selected?: Set<string>;
  onSelectChange?: (next: Set<string>) => void;
  onRowActivate?: (row: T) => void;
  rowHeight?: number;
  loading?: boolean;
  empty?: ReactNode;
  zebra?: boolean;
  className?: string;
}

function getRowHeight(fallback: number) {
  if (typeof window === "undefined") return fallback;
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--row-height").trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

export function DataGrid<T>({
  rows,
  columns,
  rowKey,
  sort,
  onSort,
  selected,
  onSelectChange,
  onRowActivate,
  rowHeight = 48,
  loading,
  empty,
  zebra,
  className,
}: DataGridProps<T>) {
  const parentRef = useRef<HTMLDivElement | null>(null);
  const selectable = !!onSelectChange;
  const effectiveHeight = getRowHeight(rowHeight);

  const virt = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => effectiveHeight,
    overscan: 12,
  });

  const checkColWidth = selectable ? "44px " : "";
  const template = `${checkColWidth}${columns.map(c => (typeof c.width === "number" ? `${c.width}px` : c.width ?? "minmax(120px, 1fr)")).join(" ")}`;
  const gridTemplate: CSSProperties = { gridTemplateColumns: template };

  const ariaSort = (key: string): "ascending" | "descending" | "none" =>
    sort?.key === key ? (sort.dir === "asc" ? "ascending" : "descending") : "none";

  const toggleSort = (key: string) => {
    if (!onSort) return;
    const dir: SortDir = sort?.key === key && sort.dir === "asc" ? "desc" : "asc";
    onSort({ key, dir });
  };

  const allSelected = selectable && rows.length > 0 && rows.every(r => selected?.has(rowKey(r)));
  const someSelected = selectable && rows.some(r => selected?.has(rowKey(r))) && !allSelected;

  const toggleAll = () => {
    if (!onSelectChange) return;
    if (allSelected) onSelectChange(new Set());
    else onSelectChange(new Set(rows.map(rowKey)));
  };

  const toggleOne = (id: string) => {
    if (!onSelectChange) return;
    const next = new Set(selected ?? []);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectChange(next);
  };

  return (
    <div className={cn("lex-grid", className)} role="grid" aria-rowcount={rows.length + 1}>
      <div className="lex-grid__head" style={gridTemplate} role="row">
        {selectable ? (
          <div className="lex-grid__th" role="columnheader" data-align="center" style={{ justifyContent: "center" }}>
            <input
              type="checkbox"
              aria-label={allSelected ? "Deselect all" : "Select all"}
              checked={allSelected}
              ref={el => { if (el) el.indeterminate = someSelected; }}
              onChange={toggleAll}
            />
          </div>
        ) : null}
        {columns.map(col => {
          const sortable = !!col.sortable && !!onSort;
          return (
            <button
              key={col.key}
              type="button"
              role="columnheader"
              aria-colindex={columns.indexOf(col) + (selectable ? 2 : 1)}
              className={cn("lex-grid__th", sortable && "lex-grid__th--sortable")}
              data-align={col.align}
              aria-sort={sortable ? ariaSort(col.key) : undefined}
              onClick={sortable ? () => toggleSort(col.key) : undefined}
              disabled={!sortable}
              style={{ background: "transparent" }}
            >
              <span>{col.label}</span>
              {sortable && sort?.key === col.key ? (
                <span className="lex-grid__sort" aria-hidden="true">{sort.dir === "asc" ? "▲" : "▼"}</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="lex-grid__body" ref={parentRef}>
        {loading ? (
          <div className="lex-grid__loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="lex-grid__empty">{empty ?? "No records"}</div>
        ) : (
          <div style={{ height: virt.getTotalSize(), position: "relative", width: "100%" }}>
            {virt.getVirtualItems().map(vi => {
              const row = rows[vi.index];
              const id = rowKey(row);
              const isSelected = selected?.has(id) ?? false;
              return (
                <div
                  key={id}
                  role="row"
                  aria-rowindex={vi.index + 2}
                  aria-selected={selectable ? isSelected : undefined}
                  tabIndex={0}
                  className={cn(
                    "lex-grid__row",
                    zebra && vi.index % 2 === 1 && "lex-grid__row--zebra",
                    isSelected && "lex-grid__row--selected",
                  )}
                  style={{
                    ...gridTemplate,
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    transform: `translateY(${vi.start}px)`,
                    height: vi.size,
                  }}
                  onClick={() => onRowActivate?.(row)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); onRowActivate?.(row); }
                    if (e.key === " " && selectable) { e.preventDefault(); toggleOne(id); }
                  }}
                >
                  {selectable ? (
                    <div className="lex-grid__cell lex-grid__cell--check" role="gridcell" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select row ${id}`}
                        checked={isSelected}
                        onChange={() => toggleOne(id)}
                      />
                    </div>
                  ) : null}
                  {columns.map(col => (
                    <div
                      key={col.key}
                      role="gridcell"
                      className="lex-grid__cell"
                      data-align={col.align}
                    >
                      {col.render ? col.render(row) : String((row as Record<string, unknown>)[col.key] ?? "")}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default DataGrid;
